import { PALETTES } from "./palettes";

import type { Material } from "./palettes";

/* ----------------------------------------------------------------------------
   Palette recoloring.

   LPC sheets are drawn in one base ramp — skin in `light`, hair in `orange` —
   and every other color is produced by swapping those exact pixels for another
   ramp's. That is why an agent can be green and a human bronze from the same
   21 KB of art, and why the wardrobe doesn't need a pre-baked variant per color.

   Matching is exact RGB with ±1 tolerance, per the generator's own spec: the
   ramps are authored colors, not gradients, so anything fuzzier would bleed
   into the outline and the eye whites.
---------------------------------------------------------------------------- */

const TOLERANCE = 1;
const cache = new Map<string, string>(); // "url|material|ramp" → data URL
const inflight = new Map<string, Promise<string>>();

const hex = (h: string): [number, number, number] => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];

/** The base ramp is what the PNGs are actually drawn in, so it needs no work. */
export function isBaseRamp(material: Material, ramp: string): boolean {
  const pal = PALETTES[material];
  if (!pal || !pal[ramp]) return true;
  return pal[ramp].every((c, i) => c.toLowerCase() === pal.$base[i]?.toLowerCase());
}

function swap(img: HTMLImageElement, material: Material, ramp: string): string {
  const pal = PALETTES[material];
  const from = pal.$base.map(hex);
  const to = pal[ramp].map(hex);

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);

  const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] === 0) continue;
    for (let c = 0; c < from.length; c++) {
      const [r, g, b] = from[c];
      if (
        Math.abs(px[i] - r) <= TOLERANCE &&
        Math.abs(px[i + 1] - g) <= TOLERANCE &&
        Math.abs(px[i + 2] - b) <= TOLERANCE
      ) {
        [px[i], px[i + 1], px[i + 2]] = to[c];
        break;
      }
    }
  }
  ctx.putImageData(data, 0, 0);
  return canvas.toDataURL();
}

/**
 * Already-resolved result, or null. Lets a mounting avatar paint on its first
 * frame instead of flashing empty while a microtask resolves — after the first
 * character of a given combination, every later one is a cache hit.
 */
export function peek(url: string, material: Material, ramp: string | null | undefined): string | null {
  if (!ramp || isBaseRamp(material, ramp)) return url;
  return cache.get(`${url}|${material}|${ramp}`) ?? null;
}

/**
 * Recolor `url` from its base ramp into `ramp`. Resolves to a data URL, or to
 * `url` untouched when no recolor is needed. Memoized per (url, material, ramp)
 * — a workspace only ever uses a handful of combinations, so this runs a few
 * dozen times on first paint and never again.
 */
export function recolor(url: string, material: Material, ramp: string | null | undefined): Promise<string> {
  if (!ramp || isBaseRamp(material, ramp)) return Promise.resolve(url);
  const key = `${url}|${material}|${ramp}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key)!);
  if (inflight.has(key)) return inflight.get(key)!;

  const job = new Promise<string>((resolve) => {
    const img = new Image();
    img.onload = () => {
      let out: string;
      try {
        out = swap(img, material, ramp);
      } catch {
        out = url; // canvas unavailable — the base ramp is still a valid avatar
      }
      cache.set(key, out);
      inflight.delete(key);
      resolve(out);
    };
    // A missing sheet must not blank the avatar; fall back to the raw URL.
    img.onerror = () => {
      cache.set(key, url);
      inflight.delete(key);
      resolve(url);
    };
    img.src = url;
  });
  inflight.set(key, job);
  return job;
}
